import type { TrailRender } from "../../../shared/shop-catalog";
import { drawCustomGrid } from "./CustomArt";

export function drawTrailIcon(
  ctx: CanvasRenderingContext2D,
  cfg: TrailRender,
  x: number,
  y: number,
  alpha: number,
): void {
  ctx.globalAlpha = alpha;
  const size = (cfg.sizeMin + cfg.sizeMax) / 2;
  const color = cfg.color;
  const color2 = cfg.color2 ?? color;
  switch (cfg.kind) {
    case "rainbow":
      ctx.fillStyle = `hsl(${(x * 17) % 360}, 85%, 60%)`;
      circle(ctx, x, y, size);
      break;
    case "sparkle":
    case "magic":
    case "cosmic":
      ctx.fillStyle = color;
      star4(ctx, x, y, size * 1.2);
      break;
    case "fire":
      ctx.fillStyle = color2;
      flame(ctx, x, y, size * 1.2);
      break;
    case "neon":
    case "bubble":
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.stroke();
      break;
    case "lightning":
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(x, y - 3);
      ctx.lineTo(x - 2, y);
      ctx.lineTo(x, y + 1);
      ctx.lineTo(x - 2, y + 3);
      ctx.stroke();
      break;
    case "smoke":
    case "shadow":
    case "ghost":
    case "vapor":
      ctx.fillStyle = color;
      ctx.globalAlpha = alpha * 0.6;
      circle(ctx, x, y, size * 1.3);
      break;
    case "star":
      ctx.fillStyle = color;
      star5(ctx, x, y, size * 1.4, 0);
      break;
    case "pixel":
    case "cyber":
      ctx.fillStyle = color;
      ctx.fillRect(x - 1, y - 1, 2, 2);
      break;
    case "plasma":
    case "candy":
    case "gold":
      ctx.fillStyle = color;
      circle(ctx, x, y, size);
      ctx.fillStyle = color2;
      circle(ctx, x - size * 0.3, y - size * 0.3, size * 0.4);
      break;
    case "heart":
      ctx.fillStyle = color;
      heart(ctx, x, y, size * 1.4);
      break;
    case "blizzard":
      ctx.strokeStyle = color;
      ctx.lineWidth = 0.8;
      for (let j = 0; j < 3; j += 1) {
        const a = j * (Math.PI / 3);
        ctx.beginPath();
        ctx.moveTo(x - Math.cos(a) * size, y - Math.sin(a) * size);
        ctx.lineTo(x + Math.cos(a) * size, y + Math.sin(a) * size);
        ctx.stroke();
      }
      break;
    case "laser":
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x - size, y);
      ctx.lineTo(x + size, y);
      ctx.stroke();
      break;
    case "confetti":
      ctx.fillStyle = `hsl(${(x * 13) % 360}, 80%, 60%)`;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(((x * 7) % 100) / 30);
      ctx.fillRect(-size / 2, -size / 4, size, size / 2);
      ctx.restore();
      break;
    case "void":
      ctx.fillStyle = color;
      circle(ctx, x, y, size);
      ctx.strokeStyle = color2;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(x, y, size * 1.4, 0, Math.PI * 2);
      ctx.stroke();
      break;
    case "portal":
      ctx.fillStyle = color;
      circle(ctx, x, y, size);
      ctx.fillStyle = color2;
      circle(ctx, x + 2, y - 1, size * 0.5);
      break;
    case "crystal":
      ctx.fillStyle = color;
      diamond(ctx, x, y, size * 1.2);
      break;
    case "glitch":
      ctx.fillStyle = color;
      ctx.fillRect(x - size / 2, y - size / 2, size, size);
      ctx.fillStyle = color2;
      ctx.fillRect(x - size / 2 + 1, y - size / 2 + 1, size / 2, size / 2);
      break;
    case "aurora":
      ctx.fillStyle = color2;
      ctx.fillRect(x - size, y - size / 3, size * 2, size / 1.5);
      break;
    case "blossom":
      ctx.fillStyle = color;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(((x * 7) % 100) / 25);
      ctx.beginPath();
      ctx.ellipse(0, 0, size * 1.2, size * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      break;
    case "divine":
      ctx.fillStyle = color2;
      ctx.globalAlpha = alpha * 0.6;
      circle(ctx, x, y, size * 1.6);
      ctx.fillStyle = color;
      ctx.globalAlpha = alpha;
      star4(ctx, x, y, size);
      break;
    case "custom":
      if (cfg.cells && cfg.grid) {
        const s = size * 2.2;
        drawCustomGrid(ctx, cfg.cells, cfg.grid, x - s / 2, y - s / 2, s, s);
      }
      break;
    case "puff":
    default:
      ctx.fillStyle = color;
      ctx.fillRect(x - size / 2, y - size / 2, size, size);
      break;
  }
}

function circle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
}

function star4(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.lineTo(x + r * 0.3, y - r * 0.3);
  ctx.lineTo(x + r, y);
  ctx.lineTo(x + r * 0.3, y + r * 0.3);
  ctx.lineTo(x, y + r);
  ctx.lineTo(x - r * 0.3, y + r * 0.3);
  ctx.lineTo(x - r, y);
  ctx.lineTo(x - r * 0.3, y - r * 0.3);
  ctx.closePath();
  ctx.fill();
}

function star5(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  rot: number,
): void {
  ctx.beginPath();
  for (let i = 0; i < 10; i += 1) {
    const a = rot + (i * Math.PI) / 5 - Math.PI / 2;
    const rad = i % 2 === 0 ? r : r * 0.45;
    const px = x + Math.cos(a) * rad;
    const py = y + Math.sin(a) * rad;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fill();
}

function heart(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x, y + r * 0.4);
  ctx.bezierCurveTo(x + r, y - r * 0.4, x + r * 0.6, y - r, x, y - r * 0.3);
  ctx.bezierCurveTo(x - r * 0.6, y - r, x - r, y - r * 0.4, x, y + r * 0.4);
  ctx.closePath();
  ctx.fill();
}

function diamond(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.lineTo(x + r * 0.7, y);
  ctx.lineTo(x, y + r);
  ctx.lineTo(x - r * 0.7, y);
  ctx.closePath();
  ctx.fill();
}

function flame(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.quadraticCurveTo(x + r * 0.7, y, x + r * 0.4, y + r * 0.5);
  ctx.quadraticCurveTo(x, y + r, x - r * 0.4, y + r * 0.5);
  ctx.quadraticCurveTo(x - r * 0.7, y, x, y - r);
  ctx.fill();
}
